
import React, { useState, useEffect, useRef } from 'react';
import { Button, Badge } from '../components/UI';
import { apiRequest } from '../utils/api';
import { useLeague } from '../components/LeagueContext';
import { CheckCircle, AlertTriangle, Zap, RefreshCw, FileJson, Save, Flag, X, Upload, Clock, Timer, Shield } from 'lucide-react';

interface DbDriver { user_id: string; username: string; eaId?: string; ea_id?: string; teamId?: string; team_id?: string; teamName?: string; team_name?: string; teamColor?: string; team_color?: string; }

interface MatchedRow {
    position: number; userId: string; teamId: string;
    status: string; timeDiff: string; fastestLap: boolean; cleanRace: boolean;
    qualiPosition: number; qualiTime: string;
    sprintPosition: number; sprintStatus: string; sprintTime: string;
    telemetryData: string;
    driverName: string; username: string; teamName: string; teamColor: string; gameTeam: string;
    numLaps: number; pitStops: number; stints: string; bestLapMs: number;
    penaltiesTime: number; collisions: number; posGain: number;
}

export const BetaResults: React.FC = () => {
    const { teams: leagueTeams } = useLeague();
    const [events, setEvents] = useState<any[]>([]);
    const [selectedEventId, setSelectedEventId] = useState('');
    const [dbDrivers, setDbDrivers] = useState<DbDriver[]>([]);
    const [existingResults, setExistingResults] = useState<any[]>([]);
    
    // Multi-JSON state
    const [raceJson, setRaceJson] = useState<any>(null);
    const [qualiJson, setQualiJson] = useState<any>(null);
    const [sprintJson, setSprintJson] = useState<any>(null);
    const [raceFile, setRaceFile] = useState('');
    const [qualiFile, setQualiFile] = useState('');
    const [sprintFile, setSprintFile] = useState('');
    
    const [rows, setRows] = useState<MatchedRow[]>([]);
    const [step, setStep] = useState<'UPLOAD' | 'REVIEW' | 'DONE'>('UPLOAD');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const raceRef = useRef<HTMLInputElement>(null);
    const qualiRef = useRef<HTMLInputElement>(null);
    const sprintRef = useRef<HTMLInputElement>(null);

    useEffect(() => { apiRequest<any[]>('events.php?scope=recent').then(r => setEvents(Array.isArray(r) ? [...r].sort((a,b) => new Date(b.date).getTime() - new Date(a.date).getTime()) : [])).catch(()=>{}); }, []);
    useEffect(() => { apiRequest<any>('standings.php').then(r => { if(r?.drivers) setDbDrivers(r.drivers.map((d:any) => ({ user_id:d.id, username:d.username, eaId:d.ea_id||d.eaId, teamId:d.teamId, teamName:d.teamName||'Reserve', teamColor:d.teamColor||'#666' }))); }).catch(()=>{}); }, []);

    // Load existing results when event changes
    useEffect(() => {
        if (!selectedEventId) { setExistingResults([]); return; }
        apiRequest<any>(`results.php?eventId=${selectedEventId}`).then(r => {
            const data = Array.isArray(r) ? r : [];
            setExistingResults(data.filter((d: any) => d.user_id));
        }).catch(() => setExistingResults([]));
    }, [selectedEventId]);

    const norm = (s: string) => s.replace(/\u00a0/g,' ').replace(/\s+/g,' ').trim().toLowerCase();
    const fmtMs = (ms: number) => { if(!ms||ms<=0) return '-'; const m=Math.floor(ms/60000); const s=((ms%60000)/1000).toFixed(3); return m>0?`${m}:${s.padStart(6,'0')}`:s; };

    const findMatch = (gt: string): DbDriver|null => {
        const g = norm(gt);
        for(const d of dbDrivers) { if(norm(d.username)===g) return d; const ea = d.eaId||d.ea_id||''; if(ea&&norm(ea)===g) return d; }
        for(const d of dbDrivers) { const n=norm(d.username); if(g.includes(n)||n.includes(g)) return d; const ea=norm(d.eaId||d.ea_id||''); if(ea&&(g.includes(ea)||ea.includes(g))) return d; }
        return null;
    };

    const loadJson = (file: File, setter: (d:any)=>void, nameSetter: (n:string)=>void) => {
        nameSetter(file.name);
        const r = new FileReader();
        r.onload = ev => {
            try {
                let d = JSON.parse(ev.target?.result as string);
                if(!d.final_classification && !d.session_history) { setError('Ungültiges JSON Format.'); return; }
                d = buildClassificationFromHistory(d);
                setter(d);
                setError('');
            } catch { setError('JSON Lesefehler.'); }
        };
        r.readAsText(file);
    };

    const getSessionType = (data: any, filename?: string): string => {
        const name = (data?.session_type_name || '').toLowerCase();
        const fname = (filename || '').toLowerCase();
        // Check filename first (more reliable for sprint)
        if (fname.includes('sprint_race') || fname.includes('sprint race')) return 'SPRINT';
        if (fname.includes('sprint_shootout') || fname.includes('short_sprint') || fname.includes('short_quali')) return 'QUALI';
        if (name.includes('sprint') && name.includes('shootout')) return 'QUALI';
        if (name.includes('sprint')) return 'SPRINT';
        if (name.includes('race') && !name.includes('sprint')) return 'RACE';
        if (name.includes('qual') || name.includes('short')) return 'QUALI';
        const st = data?.session_type;
        if (st >= 10 && st <= 13) return 'QUALI';
        if (st === 15) return 'RACE';
        if (st === 20) return 'SPRINT';
        return 'RACE';
    };

    // Build final_classification from lap_data_final + session_history if missing
    const buildClassificationFromHistory = (data: any): any => {
        if (data.final_classification && Array.isArray(data.final_classification) && data.final_classification.length > 0) return data;
        
        const participants = data.participants || [];
        const ldf = data.lap_data_final || [];
        const sh = data.session_history || [];
        
        if (ldf.length > 0 && participants.length > 0) {
            // Use lap_data_final (most accurate)
            const entries: any[] = [];
            ldf.forEach((entry: any, idx: number) => {
                if (!entry || !entry.car_position || entry.result_status_name === 'Invalid') return;
                const p = participants.find((x: any) => x.index === idx) || {};
                const shEntry = sh.find((s: any) => s.car_idx === idx);
                const laps = shEntry?.laps || [];
                const validLaps = laps.filter((l: any) => l && l.lap_valid && l.time_ms > 0);
                const bestLapMs = validLaps.length > 0 ? Math.min(...validLaps.map((l: any) => l.time_ms)) : 0;
                const allLaps = laps.filter((l: any) => l && l.time_ms > 0);
                const totalTime = allLaps.reduce((s: number, l: any) => s + l.time_ms, 0);
                const stints = shEntry?.tyre_stints || [];
                
                entries.push({
                    position: entry.car_position,
                    car_idx: idx,
                    driver_name: p.name || `Car ${idx}`,
                    team_name: p.team_name || '', team_color: p.team_color || '#666',
                    grid_position: entry.grid_position || 0,
                    num_laps: shEntry?.num_laps || entry.current_lap_num || 0,
                    num_pit_stops: entry.num_pit_stops || Math.max(0, stints.length - 1),
                    best_lap_ms: bestLapMs,
                    total_race_time: totalTime / 1000,
                    result_status_name: entry.result_status_name === 'Finished' ? 'Finished' : entry.result_status_name === 'Active' ? 'DNF' : entry.result_status_name || 'DNF',
                    penalties_time: entry.penalties || 0,
                    num_penalties: (entry.penalties || 0) > 0 ? 1 : 0,
                    tyre_stints: stints.map((s: any) => ({ visual_name: s.visual_name || s.compound_name, compound_name: s.compound_name, end_lap: s.end_lap }))
                });
            });
            
            data.final_classification = entries.sort((a: any, b: any) => a.position - b.position);
            return data;
        }
        
        // Fallback: session_history only (less accurate)
        if (sh.length > 0 && participants.length > 0) {
            const sorted = [...sh].sort((a: any, b: any) => {
                if (b.num_laps !== a.num_laps) return b.num_laps - a.num_laps;
                const aAll = (a.laps||[]).filter((l:any) => l.time_ms > 0);
                const bAll = (b.laps||[]).filter((l:any) => l.time_ms > 0);
                return aAll.reduce((s:number,l:any)=>s+l.time_ms,0) - bAll.reduce((s:number,l:any)=>s+l.time_ms,0);
            });
            data.final_classification = sorted.map((s: any, idx: number) => {
                const p = participants.find((x: any) => x.index === s.car_idx) || {};
                const validLaps = (s.laps||[]).filter((l:any) => l.lap_valid && l.time_ms > 0);
                const bestLapMs = validLaps.length > 0 ? Math.min(...validLaps.map((l:any) => l.time_ms)) : 0;
                return {
                    position: idx + 1, car_idx: s.car_idx,
                    driver_name: p.name || `Car ${s.car_idx}`,
                    team_name: p.team_name || '', team_color: p.team_color || '#666',
                    grid_position: idx + 1, num_laps: s.num_laps,
                    num_pit_stops: Math.max(0, (s.tyre_stints||[]).length - 1),
                    best_lap_ms: bestLapMs, total_race_time: 0,
                    result_status_name: s.num_laps >= (data.total_laps||1) ? 'Finished' : 'DNF',
                    penalties_time: 0, num_penalties: 0,
                    tyre_stints: (s.tyre_stints||[]).map((st:any) => ({ visual_name: st.visual_name||st.compound_name, compound_name: st.compound_name, end_lap: st.end_lap }))
                };
            });
        }
        return data;
    };

    const handleAutoDetect = (file: File) => {
        const r = new FileReader();
        r.onload = ev => {
            try {
                let d = JSON.parse(ev.target?.result as string);
                d = buildClassificationFromHistory(d);
                const type = getSessionType(d, file.name);
                if (type === 'QUALI') { setQualiJson(d); setQualiFile(file.name); }
                else if (type === 'SPRINT') { setSprintJson(d); setSprintFile(file.name); }
                else { setRaceJson(d); setRaceFile(file.name); }
                setError('');
            } catch { setError('JSON Lesefehler.'); }
        };
        r.readAsText(file);
    };

    const processAll = () => {
        if (!raceJson) { setError('Rennergebnis-JSON wird benötigt.'); return; }
        if (!raceJson.final_classification || !Array.isArray(raceJson.final_classification)) { setError('Keine Ergebnisdaten in der Race-JSON gefunden.'); return; }
        
        const flIdx = raceJson.fastest_lap?.car_idx ?? -1;
        
        // Find actual fastest lap by best_lap_ms (more reliable than car_idx matching)
        let fastestLapMs = Infinity;
        let fastestLapDriverIdx = -1;
        raceJson.final_classification.forEach((c: any, idx: number) => {
            if (c.best_lap_ms > 0 && c.best_lap_ms < fastestLapMs && c.result_status_name === 'Finished') {
                fastestLapMs = c.best_lap_ms;
                fastestLapDriverIdx = idx;
            }
        });
        const leaderTime = raceJson.final_classification[0]?.total_race_time || 0;
        
        // Build collision map
        const cm: Record<number,number> = {};
        (raceJson.collisions||[]).forEach((c:any) => { cm[c.car1_idx]=(cm[c.car1_idx]||0)+1; cm[c.car2_idx]=(cm[c.car2_idx]||0)+1; });

        // Build quali lookup: car_idx -> { position, best_lap_ms }
        const qualiLookup: Record<string, { position: number; bestLapMs: number }> = {};
        if (qualiJson?.final_classification) {
            qualiJson.final_classification.forEach((q: any) => {
                const name = norm(q.driver_name);
                qualiLookup[name] = { position: q.position, bestLapMs: q.best_lap_ms };
            });
        }

        // Build sprint lookup: driver_name -> { position, status, best_lap_ms }
        const sprintLookup: Record<string, { position: number; status: string; bestLapMs: number }> = {};
        if (sprintJson?.final_classification) {
            sprintJson.final_classification.forEach((s: any) => {
                const name = norm(s.driver_name);
                sprintLookup[name] = { position: s.position, status: s.result_status_name === 'Finished' ? 'FINISHED' : 'DNF', bestLapMs: s.best_lap_ms };
            });
        }

        const newRows: MatchedRow[] = raceJson.final_classification.map((c: any, idx: number) => {
            const m = findMatch(c.driver_name);
            const col = cm[c.car_idx] || 0;
            const isFL = idx === fastestLapDriverIdx;
            const stints = (c.tyre_stints || []).map((s: any) => s.visual_name || s.compound_name || '?').join(' → ');
            const td = c.position === 1 ? 'LEADER' : c.result_status_name !== 'Finished' ? c.result_status_name : `+${(c.total_race_time - leaderTime).toFixed(3)}`;
            
            // Clean Race: ONLY if driver has actual time penalties (penalties_time > 0)
            // num_penalties alone (warnings) does NOT remove clean race
            const penTime = parseInt(c.penalties_time) || 0;
            const cleanRace = penTime === 0;

            // Quali data
            const driverNorm = norm(c.driver_name);
            const qData = qualiLookup[driverNorm];
            const qualiPos = qData?.position || c.grid_position;
            const qualiTime = qData ? fmtMs(qData.bestLapMs) : '';

            // Sprint data
            const sData = sprintLookup[driverNorm];

            // Auto-match team: if driver has no DB team, match game team to league team
            let matchedTeamId = m?.teamId || m?.team_id || '';
            let matchedTeamName = m?.teamName || m?.team_name || '';
            let matchedTeamColor = m?.teamColor || m?.team_color || c.team_color || '#666';
            
            if (!matchedTeamId && c.team_name && leagueTeams.length > 0) {
                const gameTeamLower = (c.team_name || '').toLowerCase();
                const teamMatch = leagueTeams.find(t => {
                    const tn = (t.name || '').toLowerCase();
                    return tn === gameTeamLower || tn.includes(gameTeamLower) || gameTeamLower.includes(tn);
                });
                if (teamMatch) {
                    matchedTeamId = teamMatch.id;
                    matchedTeamName = teamMatch.name;
                    matchedTeamColor = teamMatch.color || matchedTeamColor;
                }
            }

            return {
                position: c.position, userId: m?.user_id || '', teamId: matchedTeamId,
                status: c.result_status_name === 'Finished' ? 'FINISHED' : 'DNF', timeDiff: td,
                fastestLap: isFL, cleanRace,
                qualiPosition: qualiPos, qualiTime,
                sprintPosition: sData?.position || 0, sprintStatus: sData?.status || 'FINISHED', sprintTime: sData ? fmtMs(sData.bestLapMs) : '',
                telemetryData: JSON.stringify({ stints: (c.tyre_stints||[]).map((s:any)=>({tyre:s.visual_name||s.compound_name,laps:s.end_lap})), penalties:c.penalties_time, collisions:col, pit_stops:c.num_pit_stops, best_lap_ms:c.best_lap_ms }),
                driverName: c.driver_name.replace(/\u00a0/g,' '), username: m?.username || '', teamName: matchedTeamName, teamColor: matchedTeamColor,
                gameTeam: c.team_name, numLaps: c.num_laps, pitStops: c.num_pit_stops, stints,
                bestLapMs: c.best_lap_ms, penaltiesTime: parseInt(c.penalties_time) || 0, collisions: col, posGain: (qData?.position || c.grid_position) - c.position
            } as MatchedRow;
        });
        setRows(newRows);
        setStep('REVIEW');
    };

    const updateDriver = (i:number,uid:string) => { const d=dbDrivers.find(x=>x.user_id===uid); setRows(p=>p.map((r,j)=>j===i?{...r,userId:uid,username:d?.username||'',teamId:d?.teamId||d?.team_id||'',teamName:d?.teamName||d?.team_name||'',teamColor:d?.teamColor||d?.team_color||r.teamColor}:r)); };
    const toggleFL = (i:number) => { setRows(p=>p.map((r,j)=>({...r,fastestLap:j===i?!r.fastestLap:false}))); };
    const toggleClean = (i:number) => { setRows(p=>p.map((r,j)=>j===i?{...r,cleanRace:!r.cleanRace}:r)); };
    const setStatus = (i:number,s:string) => { setRows(p=>p.map((r,j)=>j===i?{...r,status:s}:r)); };

    const handleSubmit = async () => {
        if(!selectedEventId) return; setSaving(true); setError('');
        try {
            const res = rows.filter(r=>r.userId).map(r=>({ user_id:r.userId, team_id:r.teamId, position:r.position, quali_position:r.qualiPosition, quali_time:r.qualiTime, status:r.status, fastest_lap:r.fastestLap, clean_race_bonus:r.cleanRace, time_diff:r.timeDiff==='LEADER'?'0.000':r.timeDiff, sprint_position:r.sprintPosition, sprint_status:r.sprintStatus, sprint_time:r.sprintTime, telemetry_data:r.telemetryData }));
            await apiRequest('results.php','POST',{eventId:selectedEventId,results:res});
            
            // Also save full race JSON to telemetry_archives for Race Analytics
            if (raceJson) {
                try {
                    const evt = events.find(e => e.id === selectedEventId);
                    const archiveName = `${evt?.trackLocation || 'Race'}_R${evt?.roundNumber || ''}`;
                    await apiRequest('telemetry.php', 'POST', { 
                        action: 'upload_and_link', 
                        name: archiveName, 
                        eventId: selectedEventId, 
                        data: raceJson 
                    });
                } catch(archErr) { 
                    console.warn('Telemetrie-Archiv konnte nicht gespeichert werden:', archErr); 
                }
            }
            
            setStep('DONE');
        } catch(e:any){setError(e.message||'Fehler');} finally{setSaving(false);}
    };

    const mc = rows.filter(r=>r.userId).length;
    const selEvt = events.find(e=>e.id===selectedEventId);
    const hasSprint = rows.some(r => r.sprintPosition > 0);

    return (
        <div className="max-w-7xl mx-auto space-y-6 animate-fade-in pb-20">
            {/* Header */}
            <div className="relative overflow-hidden rounded-xl border border-gray-800/40 bg-gradient-to-br from-[#12121a] via-[#15151e] to-[#12121a] p-8">
                <div className="absolute top-0 right-0 w-48 h-48 bg-blue-500/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/4 pointer-events-none" />
                <div className="flex justify-between items-end">
                    <div className="flex items-center gap-3"><div className="w-1.5 h-10 bg-blue-500 rounded-full" /><div><h1 className="text-3xl md:text-4xl font-black uppercase italic tracking-tighter text-white leading-none">Beta Ergebnisse</h1><p className="text-[10px] text-gray-500 font-bold uppercase tracking-[0.3em] mt-1">Telemetrie-Import · Quali + Rennen + Sprint</p></div></div>
                    <Badge className="bg-blue-500/10 text-blue-400 border-blue-500/30 text-[10px]">BETA</Badge>
                </div>
            </div>

            {error && <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 text-red-400 text-sm flex items-center gap-2"><AlertTriangle size={16}/>{error}<button onClick={()=>setError('')} className="ml-auto text-red-600 hover:text-red-400"><X size={14}/></button></div>}

            {/* UPLOAD STEP */}
            {step === 'UPLOAD' && (
                <div className="space-y-4">
                    {/* Event Selector */}
                    <div className="rounded-xl border border-gray-800/40 bg-[#12121a] p-6">
                        <label className="text-[10px] font-bold text-gray-500 uppercase tracking-wider block mb-2">Event auswählen</label>
                        <select className="w-full bg-[#0e0e16] border border-gray-800/40 rounded-lg p-3 text-white text-sm font-bold outline-none focus:border-blue-500" value={selectedEventId} onChange={e=>setSelectedEventId(e.target.value)}>
                            <option value="">-- Event wählen --</option>
                            {events.map(ev => <option key={ev.id} value={ev.id}>R{ev.roundNumber}: {ev.trackLocation} ({ev.status})</option>)}
                        </select>
                        {selectedEventId && (
                            <button className="mt-3 text-[10px] text-red-500 hover:text-red-400 font-bold uppercase tracking-wider" onClick={async () => {
                                if (!confirm('Alle Ergebnisse für dieses Event löschen?')) return;
                                try {
                                    await apiRequest(`results.php?eventId=${selectedEventId}`, 'DELETE');
                                    alert('Ergebnisse zurückgesetzt.');
                                } catch (e: any) { alert('Fehler: ' + e.message); }
                            }}>Ergebnisse zurücksetzen</button>
                        )}
                    </div>

                    {/* Existing Results */}
                    {existingResults.length > 0 && (
                        <div className="rounded-xl border border-green-500/20 bg-green-500/5 overflow-hidden">
                            <div className="px-4 py-2.5 border-b border-green-500/10 flex items-center justify-between">
                                <span className="text-[10px] font-bold text-green-400 uppercase tracking-wider flex items-center gap-1.5"><CheckCircle size={12}/> Ergebnisse ({existingResults.length} Fahrer)</span>
                                <div className="flex gap-2">
                                    <button className="text-[9px] text-green-400 hover:text-green-300 font-bold flex items-center gap-1" onClick={async () => {
                                        try {
                                            const updates = existingResults.map((r: any) => ({
                                                user_id: r.user_id, position: r.position, status: r.status,
                                                fastest_lap: r.fastest_lap, clean_race_bonus: r.clean_race_bonus,
                                                sprint_position: r.sprint_position || 0, team_id: r.team_id
                                            }));
                                            await apiRequest('results.php', 'PUT', { eventId: selectedEventId, results: updates });
                                            alert('Ergebnisse gespeichert!');
                                        } catch (e: any) { alert('Fehler: ' + e.message); }
                                    }}><Save size={10}/> Speichern</button>
                                    <button className="text-[9px] text-red-500 hover:text-red-400 font-bold" onClick={async () => {
                                        if (!confirm('Alle Ergebnisse löschen?')) return;
                                        try { await apiRequest(`results.php?eventId=${selectedEventId}`, 'DELETE'); setExistingResults([]); alert('Zurückgesetzt.'); } catch (e: any) { alert(e.message); }
                                    }}>Zurücksetzen</button>
                                </div>
                            </div>
                            <div className="overflow-x-auto">
                                <table className="w-full text-left">
                                    <thead><tr className="text-[8px] font-bold text-gray-600 uppercase tracking-wider">
                                        <th className="px-3 py-2 w-10">Pos</th><th className="px-3 py-2">Fahrer</th><th className="px-3 py-2">Team</th>
                                        <th className="px-3 py-2">Status</th><th className="px-3 py-2">Punkte</th>
                                        <th className="px-3 py-2 text-center">FL</th><th className="px-3 py-2 text-center">CR</th><th className="px-3 py-2 w-14">→ Pos</th>
                                    </tr></thead>
                                    <tbody>
                                        {[...existingResults].sort((a: any, b: any) => a.position - b.position).map((r: any, idx: number) => (
                                            <tr key={r.user_id} className="border-t border-gray-800/10 text-[11px]">
                                                <td className="px-3 py-1.5"><span className={`font-mono font-bold ${r.position<=3?'text-yellow-400':'text-gray-500'}`}>{r.position}</span></td>
                                                <td className="px-3 py-1.5 font-bold text-white">{r.username}</td>
                                                <td className="px-3 py-1.5">
                                                    <select className="bg-transparent border border-gray-800/40 rounded px-1 py-0.5 text-[10px] outline-none" style={{color: r.teamColor || '#666'}} value={r.team_id || ''} onChange={e => {
                                                        const t = leagueTeams.find(t => t.id === e.target.value);
                                                        setExistingResults(prev => prev.map((x: any) => x.user_id === r.user_id ? {...x, team_id: e.target.value, teamName: t?.name||'-', teamColor: t?.color||'#666'} : x));
                                                    }}>
                                                        <option value="">Kein Team</option>
                                                        {leagueTeams.map(t => <option key={t.id} value={t.id} style={{color: t.color}}>{t.name}</option>)}
                                                    </select>
                                                </td>
                                                <td className="px-3 py-1.5">
                                                    <select className="bg-transparent border border-gray-800/40 rounded px-1 py-0.5 text-[10px] text-white outline-none" value={r.status} onChange={e => {
                                                        setExistingResults(prev => prev.map((x: any) => x.user_id === r.user_id ? {...x, status: e.target.value} : x));
                                                    }}><option value="FINISHED">FIN</option><option value="DNF">DNF</option><option value="DSQ">DSQ</option></select>
                                                </td>
                                                <td className="px-3 py-1.5 font-mono text-white">{parseFloat(r.points||0) + parseFloat(r.sprint_points||0)}</td>
                                                <td className="px-3 py-1.5 text-center">
                                                    <input type="checkbox" checked={!!r.fastest_lap} onChange={() => {
                                                        setExistingResults(prev => prev.map((x: any) => x.user_id === r.user_id ? {...x, fastest_lap: !x.fastest_lap} : {...x, fastest_lap: false}));
                                                    }} className="w-3 h-3 rounded bg-gray-800 border-gray-600 text-purple-500 focus:ring-0 cursor-pointer"/>
                                                </td>
                                                <td className="px-3 py-1.5 text-center">
                                                    <input type="checkbox" checked={!!r.clean_race_bonus} onChange={() => {
                                                        setExistingResults(prev => prev.map((x: any) => x.user_id === r.user_id ? {...x, clean_race_bonus: !x.clean_race_bonus} : x));
                                                    }} className="w-3 h-3 rounded bg-gray-800 border-gray-600 text-green-500 focus:ring-0 cursor-pointer"/>
                                                </td>
                                                <td className="px-3 py-1.5">
                                                    <input type="number" min={1} max={20} className="w-9 h-5 bg-gray-800/50 border border-gray-800/40 rounded text-[9px] text-center text-white outline-none focus:border-blue-500" value={r.position} onChange={e => {
                                                        const newPos = parseInt(e.target.value);
                                                        if (newPos < 1 || newPos > 20) return;
                                                        setExistingResults(prev => {
                                                            const sorted = [...prev].sort((a: any, b: any) => a.position - b.position);
                                                            const fromIdx = sorted.findIndex((x: any) => x.user_id === r.user_id);
                                                            const item = sorted.splice(fromIdx, 1)[0];
                                                            sorted.splice(newPos - 1, 0, item);
                                                            return sorted.map((x: any, i: number) => ({...x, position: i + 1}));
                                                        });
                                                    }}/>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}

                    {/* Multi-File Upload */}
                    <div className={`grid grid-cols-1 md:grid-cols-3 gap-4 ${!selectedEventId ? 'opacity-50 pointer-events-none' : ''}`}>
                        {/* Quali */}
                        <div className={`rounded-xl border-2 border-dashed p-6 text-center cursor-pointer transition-all ${qualiJson ? 'border-green-500/30 bg-green-500/5' : 'border-gray-800/40 bg-[#12121a] hover:border-blue-500/30 hover:bg-blue-500/5'}`}
                            onClick={() => qualiRef.current?.click()}>
                            <input ref={qualiRef} type="file" accept=".json" className="hidden" onChange={e => { if(e.target.files?.[0]) loadJson(e.target.files[0], setQualiJson, setQualiFile); }} />
                            <Timer size={28} className={`mx-auto mb-2 ${qualiJson ? 'text-green-400' : 'text-gray-600'}`} />
                            <p className="text-xs font-bold text-white">Qualifying</p>
                            <p className="text-[9px] text-gray-600 mt-1">Optional</p>
                            {qualiFile && <p className="text-[10px] text-green-400 mt-2 font-mono truncate">{qualiFile}</p>}
                            {qualiJson && <CheckCircle size={14} className="text-green-500 mx-auto mt-2" />}
                        </div>

                        {/* Race */}
                        <div className={`rounded-xl border-2 border-dashed p-6 text-center cursor-pointer transition-all ${raceJson ? 'border-green-500/30 bg-green-500/5' : 'border-f1-red/30 bg-f1-red/5 hover:bg-f1-red/10'}`}
                            onClick={() => raceRef.current?.click()}>
                            <input ref={raceRef} type="file" accept=".json" className="hidden" onChange={e => { if(e.target.files?.[0]) loadJson(e.target.files[0], setRaceJson, setRaceFile); }} />
                            <Flag size={28} className={`mx-auto mb-2 ${raceJson ? 'text-green-400' : 'text-f1-red'}`} />
                            <p className="text-xs font-bold text-white">Rennen</p>
                            <p className="text-[9px] text-f1-red mt-1">Pflicht</p>
                            {raceFile && <p className="text-[10px] text-green-400 mt-2 font-mono truncate">{raceFile}</p>}
                            {raceJson && <CheckCircle size={14} className="text-green-500 mx-auto mt-2" />}
                        </div>

                        {/* Sprint */}
                        <div className={`rounded-xl border-2 border-dashed p-6 text-center cursor-pointer transition-all ${sprintJson ? 'border-green-500/30 bg-green-500/5' : 'border-gray-800/40 bg-[#12121a] hover:border-yellow-500/30 hover:bg-yellow-500/5'}`}
                            onClick={() => sprintRef.current?.click()}>
                            <input ref={sprintRef} type="file" accept=".json" className="hidden" onChange={e => { if(e.target.files?.[0]) loadJson(e.target.files[0], setSprintJson, setSprintFile); }} />
                            <Zap size={28} className={`mx-auto mb-2 ${sprintJson ? 'text-green-400' : 'text-gray-600'}`} />
                            <p className="text-xs font-bold text-white">Sprint</p>
                            <p className="text-[9px] text-gray-600 mt-1">Optional</p>
                            {sprintFile && <p className="text-[10px] text-green-400 mt-2 font-mono truncate">{sprintFile}</p>}
                            {sprintJson && <CheckCircle size={14} className="text-green-500 mx-auto mt-2" />}
                        </div>
                    </div>

                    {/* Auto-detect drop zone */}
                    <div className={`rounded-xl border border-gray-800/40 bg-[#0e0e16] p-4 text-center ${!selectedEventId ? 'opacity-50 pointer-events-none' : ''}`}>
                        <p className="text-[10px] text-gray-600 mb-2">Oder: JSON hier ablegen – Session wird automatisch erkannt</p>
                        <input type="file" accept=".json" multiple className="text-xs text-gray-500 file:mr-3 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:text-xs file:font-bold file:bg-blue-500/10 file:text-blue-400 hover:file:bg-blue-500/20 cursor-pointer"
                            onChange={e => { if(e.target.files) Array.from(e.target.files).forEach(f => handleAutoDetect(f)); }} />
                    </div>

                    <div className="flex justify-between items-center">
                        <span className="text-[10px] text-gray-600">DB Fahrer: <strong className="text-gray-400">{dbDrivers.length}</strong></span>
                        <Button onClick={processAll} disabled={!raceJson || !selectedEventId} icon={Flag}>
                            Ergebnisse verarbeiten
                        </Button>
                    </div>
                </div>
            )}

            {/* REVIEW STEP */}
            {step === 'REVIEW' && raceJson && (
                <div className="space-y-4">
                    <div className="rounded-xl border border-gray-800/40 bg-[#12121a] p-4 flex flex-wrap items-center gap-3 text-[10px] font-bold uppercase tracking-wider">
                        <Flag size={12} className="text-f1-red"/><span className="text-white">{raceJson.track_name}</span><span className="text-gray-700">·</span>
                        <span className="text-gray-400">{raceJson.total_laps} Rdn</span>
                        {qualiJson && <><span className="text-gray-700">·</span><span className="text-blue-400">Quali ✓</span></>}
                        {sprintJson && <><span className="text-gray-700">·</span><span className="text-yellow-400">Sprint ✓</span></>}
                        <div className="ml-auto flex gap-3"><span className="text-green-400">{mc} zugeordnet</span>{rows.length-mc>0&&<span className="text-yellow-500">{rows.length-mc} offen</span>}</div>
                    </div>

                    <div className="rounded-xl border border-gray-800/40 bg-[#12121a] overflow-hidden">
                        <div className="h-[2px] bg-gradient-to-r from-blue-500 via-blue-500/50 to-transparent"/>
                        <div className="overflow-x-auto">
                            <table className="w-full text-left">
                                <thead><tr className="bg-[#0e0e16] text-[9px] font-bold text-gray-600 uppercase tracking-wider">
                                    <th className="px-3 py-3 w-10">Pos</th>
                                    <th className="px-3 py-3">Gamertag</th>
                                    <th className="px-3 py-3">→ Zuordnung</th>
                                    <th className="px-3 py-3 w-14">Grid</th>
                                    {qualiJson && <th className="px-3 py-3">Quali Zeit</th>}
                                    <th className="px-3 py-3 w-20">Status</th>
                                    <th className="px-3 py-3">Zeit</th>
                                    <th className="px-3 py-3">Beste Rd.</th>
                                    {hasSprint && <th className="px-3 py-3">Sprint</th>}
                                    <th className="px-3 py-3">Strategie</th>
                                    <th className="px-3 py-3 w-8 text-center" title="Schnellste Runde">FL</th>
                                    <th className="px-3 py-3 w-8 text-center" title="Clean Race (keine Zeitstrafe)">CR</th>
                                    <th className="px-3 py-3">Info</th>
                                </tr></thead>
                                <tbody>
                                    {rows.map((r, i) => (
                                        <tr key={i} className={`border-b border-gray-800/20 ${!r.userId?'bg-yellow-500/5':'hover:bg-white/[0.02]'}`}>
                                            <td className="px-3 py-2"><span className={`font-mono font-black text-sm ${r.position<=3?'text-yellow-400':'text-gray-500'}`}>{r.position}</span></td>
                                            <td className="px-3 py-2"><div className="text-xs font-bold text-white">{r.driverName}</div><div className="text-[9px]" style={{color:r.teamColor}}>{r.gameTeam}</div></td>
                                            <td className="px-3 py-2">
                                                <select className={`bg-[#0e0e16] border rounded p-1 text-[11px] text-white w-full max-w-[180px] outline-none ${r.userId?'border-green-500/30':'border-yellow-500/30'}`} value={r.userId} onChange={e=>updateDriver(i,e.target.value)}>
                                                    <option value="">-- Kein Match --</option>
                                                    {dbDrivers.map(d => <option key={d.user_id} value={d.user_id}>{d.username} ({d.teamName||d.team_name||'-'})</option>)}
                                                </select>
                                                {r.userId && !r.teamId && (
                                                    <select className="bg-[#0e0e16] border border-orange-500/30 rounded p-1 text-[10px] text-orange-400 w-full max-w-[180px] outline-none mt-1" value={r.teamId} onChange={e => setRows(p => p.map((row,j) => j===i ? {...row, teamId: e.target.value, teamName: leagueTeams.find(t=>t.id===e.target.value)?.name||'', teamColor: leagueTeams.find(t=>t.id===e.target.value)?.color||'#666'} : row))}>
                                                        <option value="">⚠ Team wählen (EF)</option>
                                                        {leagueTeams.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                                                    </select>
                                                )}
                                            </td>
                                            <td className="px-3 py-2"><span className="font-mono text-xs text-gray-500">P{r.qualiPosition}</span>{r.posGain!==0&&<span className={`text-[9px] ml-1 ${r.posGain>0?'text-green-500':'text-red-500'}`}>{r.posGain>0?`↑${r.posGain}`:`↓${Math.abs(r.posGain)}`}</span>}</td>
                                            {qualiJson && <td className="px-3 py-2 font-mono text-[11px] text-blue-400">{r.qualiTime || '-'}</td>}
                                            <td className="px-3 py-2"><select className="bg-[#0e0e16] border border-gray-800/40 rounded p-1 text-[10px] text-white outline-none" value={r.status} onChange={e=>setStatus(i,e.target.value)}><option value="FINISHED">FIN</option><option value="DNF">DNF</option><option value="DSQ">DSQ</option></select></td>
                                            <td className="px-3 py-2 font-mono text-[11px] text-gray-400">{r.timeDiff==='LEADER'?<span className="text-yellow-400 font-bold">Leader</span>:r.timeDiff}</td>
                                            <td className="px-3 py-2 font-mono text-[11px] text-gray-400">{fmtMs(r.bestLapMs)}</td>
                                            {hasSprint && <td className="px-3 py-2">{r.sprintPosition > 0 ? <span className="font-mono text-xs text-yellow-400">P{r.sprintPosition}</span> : <span className="text-gray-700">-</span>}</td>}
                                            <td className="px-3 py-2 text-[10px] text-gray-500">{r.stints||'-'} <span className="text-gray-700">({r.pitStops}S)</span></td>
                                            <td className="px-3 py-2 text-center"><input type="checkbox" checked={r.fastestLap} onChange={()=>toggleFL(i)} className="w-3.5 h-3.5 rounded bg-gray-800 border-gray-600 text-purple-500 focus:ring-0 cursor-pointer"/></td>
                                            <td className="px-3 py-2 text-center"><input type="checkbox" checked={r.cleanRace} onChange={()=>toggleClean(i)} className="w-3.5 h-3.5 rounded bg-gray-800 border-gray-600 text-green-500 focus:ring-0 cursor-pointer"/></td>
                                            <td className="px-3 py-2 text-[9px] text-gray-600">{r.penaltiesTime>0&&<span className="text-red-400 mr-1">{r.penaltiesTime}s</span>}{r.collisions>0&&<span className="text-yellow-500">{r.collisions}x Koll.</span>}{r.penaltiesTime===0&&r.collisions===0&&<span className="text-green-600">Clean</span>}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {raceJson.collisions?.length>0 && (
                        <details className="rounded-xl border border-gray-800/40 bg-[#12121a] overflow-hidden">
                            <summary className="px-5 py-3 cursor-pointer text-[10px] font-bold text-gray-500 uppercase tracking-wider hover:text-white">Kollisionen ({raceJson.collisions.length})</summary>
                            <div className="px-5 pb-4 space-y-1 max-h-48 overflow-y-auto text-[11px] text-gray-400">
                                {raceJson.collisions.map((c:any,i:number)=><div key={i} className="flex items-center gap-2"><span className="text-gray-700 font-mono w-6">L{c.lap}</span><span className="text-white">{(c.car1||'').replace(/\u00a0/g,' ')}</span><span className="text-gray-700">↔</span><span className="text-white">{(c.car2||'').replace(/\u00a0/g,' ')}</span></div>)}
                            </div>
                        </details>
                    )}

                    <div className="flex justify-between items-center">
                        <Button variant="ghost" onClick={()=>{setStep('UPLOAD');setRows([]);}}>Zurück</Button>
                        <div className="flex items-center gap-3">
                            <span className="text-[10px] text-gray-600">{mc}/{rows.length} zugeordnet</span>
                            <Button onClick={handleSubmit} disabled={saving||mc===0} className="bg-green-600 hover:bg-green-500 border-green-500">
                                {saving?<><RefreshCw size={14} className="animate-spin mr-2"/>Speichert...</>:<><Save size={14} className="mr-2"/>Ergebnisse eintragen ({mc})</>}
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            {step === 'DONE' && (
                <div className="rounded-xl border border-green-500/30 bg-green-500/5 p-12 text-center">
                    <CheckCircle size={48} className="mx-auto mb-4 text-green-500"/>
                    <h2 className="text-2xl font-black text-white uppercase italic mb-2">Ergebnisse eingetragen!</h2>
                    <p className="text-gray-400 text-sm mb-6">{mc} Fahrer für {selEvt?.trackLocation} gespeichert.</p>
                    <div className="flex justify-center gap-3">
                        <Button variant="secondary" onClick={()=>{setStep('UPLOAD');setRaceJson(null);setQualiJson(null);setSprintJson(null);setRaceFile('');setQualiFile('');setSprintFile('');setRows([]);setSelectedEventId('');}}>Neues Rennen</Button>
                        <Button onClick={()=>window.location.hash=`/event/${selectedEventId}`}>Ergebnisse ansehen</Button>
                    </div>
                </div>
            )}
        </div>
    );
};
